import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Card,
  CardActionArea,
  LinearProgress,
  Link,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  CheckCircle as CheckCircleIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import NoticeboardButton from "../../components/shared/NoticeboardButton";
import { lightTheme } from "../../styles/theme";
import { routerLinkProps } from "./routerLinkProps";

/** Shared layout for the course overview pages (Fundamentals, Creator). */

export type Crumb = { label: string; to?: string };

export const CourseBreadcrumbs: React.FC<{ crumbs: Crumb[] }> = ({ crumbs }) => (
  <Breadcrumbs
    aria-label="Breadcrumb"
    separator={<ChevronRightIcon sx={{ fontSize: 18, color: "text.secondary" }} />}
    sx={{ mt: 1, mb: 2 }}
  >
    {crumbs.map((crumb) =>
      crumb.to ? (
        <Link key={crumb.label} {...routerLinkProps(crumb.to)} underline="hover" color="text.secondary">
          {crumb.label}
        </Link>
      ) : (
        <Typography key={crumb.label} color="primary.main" sx={{ fontWeight: 600 }} aria-current="page">
          {crumb.label}
        </Typography>
      ),
    )}
  </Breadcrumbs>
);

export const CourseHero: React.FC<{
  accent: string;
  description: string;
  doneCount: number;
  total: number;
  progressLabel: string;
  progressNote?: React.ReactNode;
  image: string;
  children?: React.ReactNode;
}> = ({ accent, description, doneCount, total, progressLabel, progressNote, image, children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        borderRadius: { xs: 2, md: 3 },
        p: { xs: 2, sm: 2.5, md: 3 },
        mt: { xs: 2, md: 0 },
        border: "1px solid",
        borderColor: alpha(theme.palette.primary.main, 0.14),
        background: `radial-gradient(circle at 85% 15%, ${alpha(
          theme.palette.primary.main,
          0.12,
        )} 0%, transparent 45%), linear-gradient(135deg, ${theme.palette.gradients.heroSurface[0]} 0%, ${theme.palette.gradients.heroSurface[1]} 100%)`,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" },
        alignItems: "center",
        gap: { xs: 2, md: 3 },
        overflow: "hidden",
      }}
    >
      <Box sx={{ textAlign: { xs: "center", md: "left" }, maxWidth: { xs: "100%", md: 520 } }}>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: "2rem", md: "2.7rem" },
            lineHeight: { xs: 1.08, md: 1.04 },
            fontWeight: 400,
            letterSpacing: "-0.015em",
            color: "text.primary",
          }}
        >
          Noticeboard{" "}
          <Box component="span" sx={{ color: "primary.main" }}>
            {accent}
          </Box>
        </Typography>
        <Typography
          sx={{
            mt: 1.25,
            fontSize: { xs: "1rem", md: "1.08rem" },
            lineHeight: 1.35,
            color: "text.secondary",
          }}
        >
          {description}
        </Typography>
        <Box sx={{ mt: 2, maxWidth: 360, mx: { xs: "auto", md: 0 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.75 }}>
            {doneCount} of {total} done
          </Typography>
          <LinearProgress
            variant="determinate"
            value={total ? (doneCount / total) * 100 : 0}
            aria-label={progressLabel}
            aria-valuetext={`${doneCount} of ${total} done`}
            sx={{
              height: 8,
              borderRadius: 999,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              "& .MuiLinearProgress-bar": { borderRadius: 999 },
            }}
          />
          {progressNote}
        </Box>
        <Box sx={{ mt: 2.25, display: "flex", justifyContent: { xs: "center", md: "flex-start" } }}>
          {children}
        </Box>
      </Box>
      <Box sx={{ display: { xs: "none", md: "flex" }, justifyContent: "center", alignItems: "center", minHeight: 190 }}>
        <Box
          component="img"
          src={image}
          alt=""
          decoding="async"
          sx={{
            width: { md: 248, lg: 297 },
            maxWidth: "100%",
            objectFit: "contain",
            filter: `drop-shadow(0 8px 18px ${alpha(theme.palette.common.black, 0.16)})`,
          }}
        />
      </Box>
    </Box>
  );
};

/** The "Your next lesson" box inside the hero. */
export const NextLessonBox: React.FC<{
  first: boolean;
  title: string;
  meta: string;
  to: string;
  buttonLabel: string;
}> = ({ first, title, meta, to, buttonLabel }) => (
  <Box
    sx={{
      width: "100%",
      p: 2,
      borderRadius: 2,
      bgcolor: "background.paper",
      border: "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="overline" color="text.secondary">
      {first ? "Your first lesson" : "Your next lesson"}
    </Typography>
    <Typography component="h2" variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
      {meta}
    </Typography>
    <NoticeboardButton
      variant="primary"
      {...routerLinkProps(to)}
      sx={{ borderRadius: 999, px: 3, py: 1, fontSize: "1rem", fontWeight: 600 }}
    >
      {buttonLabel}
    </NoticeboardButton>
  </Box>
);

export const CourseFinished: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: { xs: "center", md: "flex-start" },
      gap: 1.5,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <CheckCircleIcon color="success" aria-hidden />
      <Typography sx={{ fontSize: "1rem", fontWeight: 600 }}>You've finished the course</Typography>
    </Box>
    {children}
  </Box>
);

/** A module heading with its completion count, then a grid of lesson cards. */
export const CourseModule: React.FC<{
  number?: number;
  title: string;
  doneCount: number;
  total: number;
  children: React.ReactNode;
}> = ({ number, title, doneCount, total, children }) => (
  <>
    {number !== undefined && (
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
        Module {number}
      </Typography>
    )}
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1,
        mb: 2,
      }}
    >
      <Typography component="h2" variant="h5" fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" aria-label={`${title} progress`}>
        {doneCount} of {total} completed
      </Typography>
    </Box>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
        gap: 3,
      }}
    >
      {children}
    </Box>
  </>
);

export const CourseLessonCard: React.FC<{
  number: number;
  title: string;
  intro: string;
  to: string;
  done: boolean;
  next: boolean;
  footer?: React.ReactNode;
}> = ({ number, title, intro, to, done, next, footer }) => (
  <Card
    elevation={0}
    sx={{
      border: "1px solid",
      borderColor: next ? "primary.main" : "divider",
      borderRadius: 3,
      height: "100%",
      bgcolor: "card",
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: "primary.main",
        boxShadow: `0 4px 20px ${lightTheme.colors.primaryTint15}`,
        transform: "translateY(-2px)",
      },
    }}
  >
    <CardActionArea
      component={RouterLink}
      to={to}
      sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography sx={{ color: "primary.main", fontWeight: 600, fontSize: "0.85rem" }}>
          Lesson {number}
        </Typography>
        {done ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CheckCircleIcon aria-label="Done" color="success" sx={{ fontSize: 18 }} />
            <Typography variant="caption" color="success.main">
              Completed
            </Typography>
          </Box>
        ) : next ? (
          <Typography variant="caption" color="primary.main" fontWeight={600}>
            Up next
          </Typography>
        ) : null}
      </Box>
      <Typography component="h3" variant="h6" fontWeight={600} sx={{ mt: 0.5, mb: 1, lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: footer ? 2 : 0, flex: 1, lineHeight: 1.5 }}>
        {intro}
      </Typography>
      {footer}
    </CardActionArea>
  </Card>
);
